/**
 * Накладний шар біля якоря — один примітив для меню, списків під полем і поповерів
 * (components.md, розд. 8a; stability.md, розд. 5; аудит якості № 7–10).
 *
 * - **Портал.** Шар живе поза потоком сторінки: у `body`, а якщо якір — у відкритому `<dialog>` чи
 *   в іншому шарі, то в ньому. Так шар не ріжуть `overflow` предків і не перекривають липкі
 *   заголовки, модальний діалог не робить його інертним, а «клік поза» батьківського шару не
 *   закриває його через дочірній (список поля-зв'язку в поповері фільтра).
 * - **Місце.** `placeLayer`: під якорем чи над ним, зсув біля краю вікна, висота за вільним
 *   місцем (прокрутка — усередині шару). Перерахунок — на прокрутку будь-якого предка, зміну
 *   розміру вікна, якоря чи шару; не частіше кадру.
 * - **Закриття.** `Esc` закриває верхній відкритий шар і повертає фокус на якір; клік поза шаром і
 *   якорем закриває, а фокус лишається там, куди клікнули. Клік у тості (`TOAST_ZONES`) — не «поза»:
 *   тости над шарами, «Скасувати» дії з шару його не закриває. Шар, що зник з фокусом усередині
 *   (вибір пункту), віддає фокус якорю.
 * - **Якір у «Ще».** Дію, сховану в «Ще · N» / «⋯» рядка з переповненням, людина не бачить: шар,
 *   відкритий з «Ще», ставиться від видимої кнопки «Ще» і повертає фокус на неї (`layerAnchor`,
 *   `focusAnchor` у `popover-layer.ts`; рев'ю #902). Власник шару, що сам повертає фокус, бере
 *   `focusAnchor` звідти.
 * - **Шар, що стоїть сам** (`place: 'self'`): місце дає клас шару (бічна панель центру сповіщень),
 *   примітив — портал, `Esc`, «клік поза» і фокус.
 * - **Шар** — `var(--cl-z-dropdown)` (tokens.md, «Шари»), не top layer браузера: тост і підказка
 *   мають лишатися над меню.
 * - **Лінива поведінка.** Тут — лише портал і вміст; місце, перерахунок, `Esc`, «клік поза» й фокус
 *   (`popover-layer.ts`) потрібні тільки відкритому шару, тож у перший екран не йдуть (бюджет
 *   бандла). Модуль вантажиться в простої після першого екрана (`afterPrefetchStart`), а шар,
 *   відкритий раніше, просить його сам. До завантаження шар уже в DOM (власник ставить фокус на
 *   перший пункт), але з `data-pending` — невидимий і без указівника: кадру не на своєму місці немає.
 *   Модуль прийшов — у тому самому коміті шар стає на місце і з'являється.
 *
 * Без бібліотек позиціонування (рішення PM 28.10): Floating UI не додаємо, CSS anchor positioning
 * ще не скрізь, а запасний розрахунок однаково потрібен.
 */
import {
  type RefObject,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useSyncExternalStore,
} from 'react';
import { afterPrefetchStart } from '../perf/prefetch.ts';
import { layerPortal } from './layer-portal.ts';
import {
  type AnchoredLayerOptions,
  inToastZone,
  type LayerCloseReason,
  type PopoverProps,
} from './layer-types.ts';
import './popover.css';

export type { AnchoredLayerOptions, LayerCloseReason, PopoverProps };

type LayerModule = typeof import('./popover-layer.ts');

/** Лінивий модуль шарів (див. «Лінива поведінка» в описі модуля). */
let layerModule: LayerModule | null = null;
let loading: Promise<LayerModule> | null = null;
let importLayer = (): Promise<LayerModule> => import('./popover-layer.ts');
let prefetchAsked = false;
const subscribers = new Set<() => void>();

/** Завантажити поведінку шару; повторний виклик — той самий запит. Збій — наступний спробує знову. */
export function preloadLayer(): Promise<LayerModule> {
  loading ??= importLayer().then(
    (m) => {
      layerModule = m;
      for (const notify of subscribers) notify();
      return m;
    },
    (error: unknown) => {
      loading = null;
      throw error;
    },
  );
  return loading;
}

/** Тести: модуль ще не завантажено й не заплановано; `load` — підмінне завантаження (відкладене). */
export function resetLayerForTests(load?: () => Promise<LayerModule>) {
  layerModule = null;
  loading = null;
  prefetchAsked = false;
  importLayer = load ?? (() => import('./popover-layer.ts'));
}

const subscribe = (notify: () => void) => {
  subscribers.add(notify);
  return () => subscribers.delete(notify);
};
const loadedLayer = () => layerModule;
const preloadQuietly = () => {
  // Мережа чи нова збірка: шар довантажиться, коли його відкриють.
  preloadLayer().catch(() => undefined);
};

/**
 * Лінивий модуль шарів (поведінка шару, вміст меню); `null` — ще не прийшов. Вантажиться в простої
 * після першого екрана (`afterPrefetchStart`, один планувальник на всі шари), а відкритий раніше
 * шар (`open`) просить його сам. Не завантажився, поки шар відкритий, — `onFail`: власник закриває
 * шар (невидимий шар не має чекати, доки його закриють наосліп), наступне відкриття — повтор.
 */
export function useLayerModule(open: boolean, onFail?: () => void): LayerModule | null {
  const loaded = useSyncExternalStore(subscribe, loadedLayer, loadedLayer);
  const fail = useRef(onFail);
  useLayoutEffect(() => {
    fail.current = onFail;
  });
  useEffect(() => {
    if (prefetchAsked) return;
    prefetchAsked = true;
    afterPrefetchStart(preloadQuietly);
  }, []);
  useEffect(() => {
    if (!open || loaded) return;
    let live = true;
    preloadLayer().catch(() => {
      if (live) fail.current?.();
    });
    return () => {
      live = false;
    };
  }, [open, loaded]);
  return loaded;
}

/**
 * Шар відкрито, а модуль шарів ще йде (`pending`): шар невидимий, меню ще не малюється, але `Esc` і
 * клік поза якорем і шаром закривають його вже зараз, як і з модулем. `Esc` — лише з фокусом у шарі,
 * на якорі чи ніде; далі він не йде (клавіші екрана: `Esc` картки — «до списку»), фокус — на якір.
 * Якір у «Ще» до модуля бути не може: меню «Ще» малюється лише з ним. `home` — куди фокус після
 * `Esc` і де ще він може бути (рядок списку, з якого `Shift+F10` відкрив меню «⋯», LST-23); немає —
 * якір.
 */
export function usePendingClose(
  pending: boolean,
  anchor: RefObject<HTMLElement | null>,
  layer: RefObject<HTMLElement | null> | null,
  onClose: (reason: LayerCloseReason) => void,
  home?: () => HTMLElement | null | undefined,
): void {
  const close = useRef(onClose);
  const back = useRef(home);
  useLayoutEffect(() => {
    close.current = onClose;
    back.current = home;
  });
  useLayoutEffect(() => {
    if (!pending) return;
    const mine = (target: EventTarget | null) =>
      target instanceof Node &&
      Boolean(layer?.current?.contains(target) || anchor.current?.contains(target));
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.isComposing) return;
      const t = e.target;
      const to = back.current?.();
      const own = mine(t) || (t instanceof Node && Boolean(to?.contains(t)));
      if (t !== document.body && t !== document.documentElement && t && !own) return;
      e.preventDefault();
      e.stopPropagation();
      close.current('escape');
      (to ?? anchor.current)?.focus();
    };
    const onDown = (e: Event) => {
      if (!mine(e.target) && !inToastZone(e.target)) close.current('outside');
    };
    document.addEventListener('keydown', onKey, true);
    document.addEventListener('pointerdown', onDown, true);
    return () => {
      document.removeEventListener('keydown', onKey, true);
      document.removeEventListener('pointerdown', onDown, true);
    };
  }, [pending, anchor, layer]);
}

/**
 * Шар у порталі: власник дає вміст і роль (`menu`, `dialog`, `listbox`), шар дає місце, `Esc`,
 * «клік поза» і фокус. Закритий шар не малюється.
 */
export function Popover({
  open,
  anchor,
  onClose,
  align,
  side,
  width,
  place,
  returnFocus,
  as = 'div',
  className,
  id,
  layerRef,
  children,
  ...rest
}: PopoverProps) {
  const own = useRef<HTMLElement | null>(null);
  const ownId = useId();
  const behavior = useLayerModule(open, () => {
    // Збій завантаження: шар закривається; фокус із невидимого шару — на якір.
    const inside = Boolean(own.current?.contains(document.activeElement));
    onClose('outside');
    if (inside) anchor.current?.focus({ preventScroll: true });
  });
  usePendingClose(open && !behavior, anchor, own, onClose);
  const setRef = useCallback(
    (node: HTMLElement | null) => {
      own.current = node;
      if (layerRef) layerRef.current = node;
    },
    [layerRef],
  );
  if (!open) return null;
  return (
    <>
      {layerPortal({
        as,
        className,
        id: id ?? ownId,
        setRef,
        // До завантаження поведінки шар у DOM (власник ставить фокус), але невидимий: без
        // місця біля якоря його не показуємо (stability.md, розд. 5). Шар, що стоїть сам, місце
        // вже має.
        pending: !behavior && place !== 'self',
        anchor: anchor.current,
        rest,
        children,
      })}
      {/* Після порталу: `ref` шару вже записано, коли спрацюють ефекти поведінки. */}
      {behavior && (
        <behavior.LayerBehavior
          layer={own}
          options={{ open, anchor, onClose, align, side, width, place, returnFocus }}
        />
      )}
    </>
  );
}
